import React, { useState, useEffect } from 'react';
import { BaseModal } from './BaseModal';
import { Link2, Image } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface FeatureRequestDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: {
    id: string;
    feedback: string;
    screenshots: string[];
    examples: string[];
    created_at: string;
    user_role: string;
    user_id: string;
  };
}

export function FeatureRequestDetailsModal({ isOpen, onClose, request }: FeatureRequestDetailsModalProps) {
  const [screenshotUrls, setScreenshotUrls] = useState<string[]>([]);
  const [email, setEmail] = useState<string>('Anonymous User');

  useEffect(() => {
    if (request.screenshots && request.screenshots.length > 0) {
      const urls = request.screenshots.map(filename => {
        const { data } = supabase.storage
          .from('feedback-uploads')
          .getPublicUrl(filename);
        return data.publicUrl;
      });

      setScreenshotUrls(urls);
    } else {
      setScreenshotUrls([]);
    }
  }, [request.screenshots]);

  useEffect(() => {
    async function fetchEmail() {
      if (request.user_id) {
        const { data, error } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', request.user_id)
          .single();

        if (!error && data) {
          setEmail(data.email);
        } else {
          setEmail('Unknown');
        }
      } else {
        setEmail('Anonymous User');
      }
    }

    fetchEmail();
  }, [request.user_id]);

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Feature Request Details"
      size="large"
    >
      <div className="space-y-6">
        {/* User Info */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Submitted By</h3>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                {email}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Date Submitted</h3>
              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                {new Date(request.created_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Feedback */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Feedback</h3>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{request.feedback}</p>
          </div>
        </div>

        {/* Screenshots */}
        {request.screenshots && request.screenshots.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 flex items-center">
              <Image className="h-5 w-5 mr-2" />
              Screenshots
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {screenshotUrls.map((url, index) => (
                <div key={index} className="relative group">
                  <img
                    src={url}
                    alt={`Screenshot ${index + 1}`}
                    className="w-full h-48 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 flex items-center justify-center transition-all duration-200 rounded-lg"
                  >
                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      View Full Size
                    </span>
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Example Links */}
        {request.examples && request.examples.length > 0 && (
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 flex items-center">
              <Link2 className="h-5 w-5 mr-2" />
              Example Links
            </h3>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
              {request.examples.map((example, index) => (
                <a
                  key={index}
                  href={example}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-[#5e9bb8] hover:text-[#4d8299] dark:text-[#5e9bb8] dark:hover:text-[#4d8299] transition-colors duration-200"
                >
                  {example}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
}